/**
 * Audit views (TMMAPP-0022, design §11.7 AUD-02 / AUD-03) — wire types from
 * `src/api/generated/`; normalized into the module's domain type. The owner view is full; the
 * virtual account view is whatever the API redacts. The SPA never widens a protected row.
 */
import { civilRangeToInstants } from '@/i18n/zonedCivil';
import { request } from './client';
import { personNameOrNull } from './personNames';
import type {
  AuditDetail as WireDetail,
  AuditEventView as WireEvent,
  PageResponseAuditEventView as WirePage,
} from './generated';

/** The actor kinds a member's audit view serves (TMMAPI-0185 U1), as the contract lists them. */
export type ActorKind = WireEvent['actorKind'];

export interface AuditEvent {
  id: number;
  occurredAt: string;
  actorKind: ActorKind | string;
  actorUserId: number | null;
  /**
   * The owner view's stored label; a sharer view's actor display name or `null`, never an email
   * (TMMAPI-0141). Blank is `null` too.
   */
  actorLabel: string | null;
  action: string;
  entityType: string;
  entityId: number;
  accountId: number | null;
  envelopeId: number | null;
  virtualAccountId: number | null;
  requestId: string;
  aiRequestId: number | null;
  before: unknown | null;
  after: unknown | null;
  /** Served label for the entity column and the Where cell; null when omitted. */
  entityLabel: string | null;
  amountMinor: number | null;
  currency: string | null;
  /** The row's sentence key and params (TMMAPI-0143); null when not served. tmm-app owns the sentences. */
  detail: AuditDetail | null;
}

/**
 * `key` is `audit.<action>` in lower snake case. A sharer's params are bounded by what the row
 * already shows. Only primitive params are kept.
 */
export interface AuditDetail {
  key: string;
  params: Record<string, string | number | boolean>;
}

export interface AuditPage {
  items: AuditEvent[];
  nextCursor: string | null;
}

export interface OwnerAuditQuery {
  /** Civil `YYYY-MM-DD` bounds, inclusive; sent as date-times in `timeZone` (TMMAPP-0161). */
  from?: string | undefined;
  to?: string | undefined;
  /** The person's zone (`/me` timezone): the day `from` starts and `to` ends in. */
  timeZone: string;
  action?: string | undefined;
  entityType?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
  /** The change log's groups, as the virtual account audit's (TMMAPI-0152 ask 6); only narrows. */
  kinds?: readonly ChangeLogKind[] | undefined;
}

export interface VirtualAccountAuditQuery {
  /** Civil `YYYY-MM-DD` bounds, inclusive; sent as date-times in `timeZone` (TMMAPP-0161). */
  from?: string | undefined;
  to?: string | undefined;
  /** The person's zone (`/me` timezone): the day `from` starts and `to` ends in. */
  timeZone: string;
  cursor?: string | undefined;
  /** The change log's Person filter (TMMAPI-0148). */
  actorUserId?: number | undefined;
  /** The change log's groups (TMMAPI-0148), sent as a comma list; empty or absent is every group. */
  kinds?: readonly ChangeLogKind[] | undefined;
}

/** The change log's groups (TMMAPI-0148 `ChangeLogKind`): each names a fixed set of audit actions, server-side. */
export const CHANGE_LOG_KINDS = [
  'MONEY',
  'BUDGETS',
  'ENVELOPES',
  'CATEGORIES',
  'PLANS',
  'MEETINGS',
  'MEMBERS',
] as const;
export type ChangeLogKind = (typeof CHANGE_LOG_KINDS)[number];

/** Only primitive params are kept: the wire's params are free-form JSON values. */
function normalizeDetail(w: WireDetail): AuditDetail {
  const params: Record<string, string | number | boolean> = {};
  for (const [name, param] of Object.entries(w.params)) {
    if (
      typeof param === 'string' ||
      typeof param === 'boolean' ||
      (typeof param === 'number' && Number.isFinite(param))
    ) {
      params[name] = param;
    }
  }
  return { key: w.key, params };
}

/** A list row. */
export function normalizeEvent(w: WireEvent): AuditEvent {
  return {
    id: w.id,
    occurredAt: w.occurredAt,
    actorKind: w.actorKind,
    actorUserId: w.actorUserId ?? null,
    actorLabel: personNameOrNull(w.actorLabel),
    action: w.action,
    entityType: w.entityType,
    entityId: w.entityId,
    accountId: w.accountId ?? null,
    envelopeId: w.envelopeId ?? null,
    virtualAccountId: w.virtualAccountId ?? null,
    requestId: w.requestId ?? '',
    aiRequestId: w.aiRequestId ?? null,
    before: w.before ?? null,
    after: w.after ?? null,
    entityLabel: w.entityLabel ?? null,
    amountMinor: w.amountMinor ?? null,
    currency: w.currency ?? null,
    detail: w.detail ? normalizeDetail(w.detail) : null,
  };
}

/** `{ items, page: { nextCursor } }`. */
export function normalizeAuditPage(w: WirePage): AuditPage {
  return { items: w.items.map(normalizeEvent), nextCursor: w.page.nextCursor };
}

export const auditApi = {
  /** GET /v1/me/audit — owner view (AUD-02); full before/after. */
  async listMine(q: OwnerAuditQuery): Promise<AuditPage> {
    const range = civilRangeToInstants(q, q.timeZone);
    return normalizeAuditPage(
      await request<WirePage>('GET', '/v1/me/audit', {
        query: {
          from: range.from,
          to: range.to,
          action: q.action,
          entityType: q.entityType,
          cursor: q.cursor,
          limit: q.limit,
          kinds: q.kinds !== undefined && q.kinds.length > 0 ? q.kinds.join(',') : undefined,
        },
      }),
    );
  },
  /** GET /v1/virtual-accounts/{id}/audit — protected view (AUD-03); the API redacts. */
  async listVirtualAccount(
    virtualAccountId: number,
    q: VirtualAccountAuditQuery,
  ): Promise<AuditPage> {
    const range = civilRangeToInstants(q, q.timeZone);
    return normalizeAuditPage(
      await request<WirePage>('GET', `/v1/virtual-accounts/${virtualAccountId}/audit`, {
        query: {
          from: range.from,
          to: range.to,
          cursor: q.cursor,
          actorUserId: q.actorUserId,
          kinds: q.kinds !== undefined && q.kinds.length > 0 ? q.kinds.join(',') : undefined,
        },
      }),
    );
  },
};
